/**
 * audio/recipes-impact.js — everything the player's throw lands on.
 *
 * GDD 11.2 rates impacts Critical, and the reason is per-material identity: a
 * can, a coconut and a dartboard must be distinguishable with your eyes shut.
 * Each material therefore gets its own recipe rather than a pitch-shifted
 * shared one, and `impact-hit` dispatches to them on `opts.material`.
 *
 * @module audio/recipes-impact
 */

import {
  osc, noiseBurst, fm, pluck, adsr, out, filter, arange, arand,
} from './synth.js';

/** @param {number} v @param {number} lo @param {number} hi */
function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Impact energy — how hard the thing actually hit, in units of the GDD 6.2.2
 * 8 N threshold.
 *
 * Deliberately NOT `opts.volume`: the engine already applies that on a gain
 * node wrapping the whole recipe, so reading it here would attenuate twice.
 * `force` is physical, `volume` is mix level, and they stay separate.
 */
function force(o) {
  return clamp(o.force != null ? o.force / 8 : 1, 0.25, 1.4);
}

// ─────────────────────────────────────────────────────────────────────────────
// Materials
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tin can — a bandpassed strike, three metallic partials that ring on past it,
 * and a scatter tail for the can tumbling off the shelf.
 */
export function canClatter(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.92, 1.12);

  noiseBurst(ctx, dest, {
    t0, gain: 0.26 * v, attack: 0.001, decay: 0.07,
    type: 'bandpass', freq: 1800 * k, q: 1.8, pan,
  });
  // Inharmonic partials: a dented cylinder, not a tuned bell.
  for (const [f, g, d] of [[1240, 0.13, 0.34], [1873, 0.09, 0.26], [2610, 0.06, 0.18]]) {
    osc(ctx, dest, {
      type: 'sine', freq: f * k, t0, gain: g * v, attack: 0.001, decay: d, pan,
    });
  }
  // Second and third contacts as it goes over.
  const t1 = t0 + 0.09 + arand() * 0.04;
  noiseBurst(ctx, dest, {
    t0: t1, gain: 0.14 * v, attack: 0.001, decay: 0.05,
    type: 'bandpass', freq: 2300 * k, q: 2.4, pan: pan + 0.08,
  });
  osc(ctx, dest, { type: 'sine', freq: 1520 * k, t0: t1, gain: 0.07 * v, attack: 0.001, decay: 0.16, pan });
  const t2 = t1 + 0.08 + arand() * 0.05;
  noiseBurst(ctx, dest, {
    t0: t2, gain: 0.09 * v, attack: 0.001, decay: 0.06,
    type: 'bandpass', freq: 1600 * k, q: 2.0, pan: pan - 0.08,
  });
  return t2 + 0.36;
}

/** Coconut — heavy, dead, almost no ring. GDD 6.2.2 wants real force to read. */
export function coconutThunk(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.95, 1.06);

  osc(ctx, dest, {
    type: 'sine', freq: 128 * k, freqTo: 68 * k, glide: 0.09,
    t0, gain: 0.34 * v, attack: 0.002, decay: 0.19, pan,
  });
  noiseBurst(ctx, dest, {
    color: 'brown', t0, gain: 0.22 * v, attack: 0.001, decay: 0.10,
    type: 'lowpass', freq: 640 * k, q: 1.1, pan,
  });
  // A dry woody click on top so it does not read as a kick drum.
  noiseBurst(ctx, dest, {
    t0, gain: 0.10 * v, attack: 0.0008, decay: 0.03,
    type: 'bandpass', freq: 1400 * k, q: 2.2, pan,
  });
  return t0 + 0.34;
}

/** Wood or crockery — a plate/board knock with a short tuned body. */
export function woodKnock(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.9, 1.14);

  noiseBurst(ctx, dest, {
    t0, gain: 0.20 * v, attack: 0.0008, decay: 0.035,
    type: 'bandpass', freq: 980 * k, q: 1.6, pan,
  });
  pluck(ctx, dest, { freq: 236 * k, t0, gain: 0.20 * v, decay: 0.22, damp: 2600, pan });
  osc(ctx, dest, { type: 'triangle', freq: 470 * k, t0, gain: 0.09 * v, attack: 0.001, decay: 0.1, pan });
  return t0 + 0.32;
}

/** Metal rim — the bright clang of a hoop, rail or bottle neck. */
export function rimClang(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.94, 1.1);

  fm(ctx, dest, {
    carrier: 520 * k, ratio: 1.71, index: 820, indexTo: 2,
    t0, gain: 0.20 * v, attack: 0.001, decay: 0.55, pan,
  });
  osc(ctx, dest, { type: 'sine', freq: 2340 * k, t0, gain: 0.08 * v, attack: 0.001, decay: 0.3, pan });
  noiseBurst(ctx, dest, {
    t0, gain: 0.14 * v, attack: 0.0008, decay: 0.05,
    type: 'highpass', freq: 2600 * k, q: 0.8, pan,
  });
  return t0 + 0.6;
}

/** Dart into cork — the tight thock, no ring at all. */
export function dartThunk(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.95, 1.08);

  osc(ctx, dest, {
    type: 'sine', freq: 190 * k, freqTo: 120 * k, glide: 0.05,
    t0, gain: 0.24 * v, attack: 0.001, decay: 0.09, pan,
  });
  noiseBurst(ctx, dest, {
    t0, gain: 0.16 * v, attack: 0.0006, decay: 0.028,
    type: 'bandpass', freq: 3100 * k, q: 1.4, pan,
  });
  noiseBurst(ctx, dest, {
    color: 'brown', t0, gain: 0.10 * v, attack: 0.001, decay: 0.05,
    type: 'lowpass', freq: 800, pan,
  });
  return t0 + 0.18;
}

/** Whack-a-Mole — a cartoon bonk. Pitch varies per hit on purpose. */
export function moleBonk(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o);
  const k = o.pitch || arange(0.85, 1.25);

  // The descending "boi" that makes it read as comedy rather than violence.
  osc(ctx, dest, {
    type: 'sine', freq: 760 * k, freqTo: 165 * k, glide: 0.13,
    t0, gain: 0.30 * v, attack: 0.003, decay: 0.20, pan,
  });
  osc(ctx, dest, {
    type: 'triangle', freq: 380 * k, freqTo: 110 * k, glide: 0.13,
    t0, gain: 0.14 * v, attack: 0.003, decay: 0.18, pan,
  });
  noiseBurst(ctx, dest, {
    t0, gain: 0.14 * v, attack: 0.0008, decay: 0.03,
    type: 'bandpass', freq: 1500 * k, q: 1.8, pan,
  });
  return t0 + 0.3;
}

/** A miss — lower energy than any hit, so the ear grades it instantly. */
export function impactMiss(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const v = force(o) * 0.7;
  noiseBurst(ctx, dest, {
    color: 'brown', t0, gain: 0.16 * v, attack: 0.002, decay: 0.10,
    type: 'lowpass', freq: 480, q: 0.9, pan,
  });
  osc(ctx, dest, {
    type: 'sine', freq: 98, freqTo: 62, glide: 0.08,
    t0, gain: 0.15 * v, attack: 0.003, decay: 0.14, pan,
  });
  return t0 + 0.26;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pops and water
// ─────────────────────────────────────────────────────────────────────────────

/** Balloon pop — the snap, the pitch collapse, and the confetti hiss after. */
export function balloonPop(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const k = o.pitch || arange(0.9, 1.15);

  // Snap: near-instant attack, brutally short.
  noiseBurst(ctx, dest, {
    t0, gain: 0.42, attack: 0.0004, decay: 0.022,
    type: 'highpass', freq: 900 * k, q: 0.7, pan,
  });
  osc(ctx, dest, {
    type: 'square', freq: 940 * k, freqTo: 150 * k, glide: 0.035,
    t0, gain: 0.20, attack: 0.0006, decay: 0.05, pan,
  });
  // Confetti hiss tail — the rubber and the air leaving.
  noiseBurst(ctx, dest, {
    t0: t0 + 0.02, gain: 0.11, attack: 0.006, decay: 0.34,
    type: 'highpass', freq: 3200, q: 0.6, pan,
  });
  noiseBurst(ctx, dest, {
    color: 'pink', t0: t0 + 0.03, gain: 0.07, attack: 0.01, decay: 0.26,
    type: 'bandpass', freq: 5200, freqTo: 2400, q: 0.8, pan,
  });
  return t0 + 0.42;
}

/**
 * Water splash — a broadband swoosh, then bubbles.
 *
 * The bubbles are the part that sells it: short sine blips that glide *up*,
 * because a rising pitch is what a shrinking bubble does.
 */
export function splash(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const size = clamp(o.size != null ? o.size : 1, 0.5, 2);

  noiseBurst(ctx, dest, {
    t0, gain: 0.24, attack: 0.004, decay: 0.16,
    type: 'bandpass', freq: 700, freqTo: 3400, q: 0.8, pan,
  });
  noiseBurst(ctx, dest, {
    color: 'pink', t0: t0 + 0.05, gain: 0.13, attack: 0.02, decay: 0.34,
    type: 'lowpass', freq: 2600, q: 0.7, pan,
  });
  bubbles(ctx, dest, { count: Math.round(5 * size), spread: 0.42, pan, gain: 0.9 }, t0 + 0.06);
  return t0 + 0.72;
}

/**
 * Bubbles on their own — used by the splash, the fish prizes, and the personal
 * space aquarium bed.
 */
export function bubbles(ctx, dest, o = {}, t0 = 0) {
  const pan = o.pan || 0;
  const count = clamp(Math.round(o.count != null ? o.count : 5), 1, 12);
  const spread = o.spread != null ? o.spread : 0.5;
  const gain = (o.gain != null ? o.gain : 1) * 0.14;

  let last = t0;
  for (let i = 0; i < count; i++) {
    const at = t0 + arand() * spread;
    const f = arange(320, 1150);
    osc(ctx, dest, {
      type: 'sine', freq: f, freqTo: f * arange(1.6, 2.6), glide: 0.045,
      t0: at, gain, attack: 0.003, decay: 0.06,
      pan: pan + arange(-0.2, 0.2),
    });
    last = Math.max(last, at + 0.08);
  }
  return last;
}

/**
 * Machine hum — a sustained low bed for the Coin Pusher while it is running.
 *
 * Sustained recipes still take a finite duration so the offline render can
 * prove them audible; the live path passes a long one and stops it early.
 */
export function machineHum(ctx, dest, o = {}, t0 = 0) {
  const dur = clamp(o.duration != null ? o.duration : 1.6, 0.2, 30);
  const pan = o.pan || 0;
  const g = out(ctx, dest, pan);
  const lp = filter(ctx, 'lowpass', 260, 1.2);
  lp.connect(g);

  for (const [f, type, amt] of [[55, 'sawtooth', 1], [55.6, 'sawtooth', 0.8], [110, 'triangle', 0.35]]) {
    const n = ctx.createOscillator();
    n.type = type;
    n.frequency.value = f;
    const ng = ctx.createGain();
    ng.gain.value = amt;
    n.connect(ng);
    ng.connect(lp);
    n.start(t0);
    n.stop(t0 + dur + 0.3);
  }
  // Motor wobble.
  const wob = ctx.createOscillator();
  wob.type = 'sine';
  wob.frequency.value = 6.5;
  const wobGain = ctx.createGain();
  wobGain.gain.value = 22;
  wob.connect(wobGain);
  wobGain.connect(lp.frequency);
  wob.start(t0);
  wob.stop(t0 + dur + 0.3);

  noiseBurst(ctx, g, {
    color: 'brown', t0, gain: 0.05, attack: 0.15, hold: dur, decay: 0.25,
    type: 'lowpass', freq: 420,
  });

  return adsr(g.gain, t0, { peak: 0.13, attack: 0.18, hold: dur, decay: 0.28 });
}
